import React, { Fragment, useEffect, useState } from 'react';
import styles from './ListTable.module.scss';
import classNames from 'classnames/bind';

const classes = classNames.bind(styles);
interface props {
    rows: Record<string, any>[] | null;
    actions?: Record<string, () => any> | Map<string, () => any> | null;
    headCells: HeadCell[];
    addSpace?: boolean;
    sortMethod?(arg0: any, arg1: string | number): number;
}

export interface HeadCell {
    id: string;
    label: string;
    numeric: boolean;
    customCell?(arg0: any, key: any): React.ReactNode;
}

const TableHeader = ({ headCells }: { headCells: HeadCell[] }) => {
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortedColumn, setSortedColumn] = useState('');

    const toggleSortDirection = (column: HeadCell) => {
        if (column.id === sortedColumn) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortDirection('asc');
            setSortedColumn(column.id);
        }
    };

    return (
        <>
            {headCells.map((headCell, index) => {
                const column = index++;
                return (
                    <div
                        key={`head-cell-${index}`}
                        className={classes('header-cell', 'cell')}
                        onClick={toggleSortDirection.bind(this, headCell)}
                        style={{
                            ['--list-table-row' as any]: 1,
                            ['--list-table-column' as any]: index, // Add 1 to index to account for grid position starting at 1
                        }}
                    >
                        <span>{headCell.label}</span>
                    </div>
                );
            })}
            <div
                key={`head-cell-${headCells.length + 1}`}
                className={classes('header-cell', 'cell')}
                style={{
                    ['--list-table-row' as any]: 1,
                    ['--list-table-column' as any]: headCells.length + 1, // Add 1 to index to account for grid position starting at 1
                }}
            />
        </>
    );
};

const TableBody = ({
    rows,
    headCells,
    actions,
}: {
    rows: Record<string, any>[] | null;
    headCells: HeadCell[];
    actions?: Record<string, () => any> | null;
}) => {
    return (
        <>
            {rows &&
                rows.length > 0 &&
                rows.map((row, rowIndex) => {
                    rowIndex += 2; // Add 2 to rowIndex to account for header row and grid position starting at 1
                    return (
                        <Fragment key={rowIndex}>
                            {headCells.map((headCell, headCellIndex) => {
                                headCellIndex++; // Add 1 to index to account for header row and grid position starting at 1
                                return (
                                    <div
                                        key={`cell-${rowIndex}-${headCellIndex}`}
                                        data-key={`cell-${rowIndex}-${headCellIndex}`}
                                        className={classes('row-cell', 'cell', { odd: rowIndex % 2 === 0 })}
                                        style={{
                                            ['--list-table-row' as any]: rowIndex,
                                            ['--list-table-column' as any]: headCellIndex, // Add 1 to index to account for grid position starting at 1
                                        }}
                                    >
                                        {headCell.customCell &&
                                            headCell.customCell(row, `custom-cell-${rowIndex}-${headCellIndex}`)}
                                        {!headCell.customCell && row[headCell.id]}
                                    </div>
                                );
                            })}
                            {actions && (
                                <div
                                    className={classes('row-cell', 'cell', { odd: rowIndex % 2 === 0 })}
                                    key={`${rowIndex}-actions`}
                                    style={{
                                        ['--list-table-row' as any]: rowIndex,
                                        ['--list-table-column' as any]: headCells.length++, // Add 1 to length to account for grid position starting at 1
                                    }}
                                >
                                    {Object.keys(actions).map((action, index) => (
                                        <button key={`action-${rowIndex}-${index}`} onClick={actions[action]}>
                                            {action}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div
                                className={classes('row-cell', 'cell', { odd: rowIndex % 2 === 0 })}
                                key={`${rowIndex}-empty`}
                                style={{
                                    ['--list-table-row' as any]: rowIndex,
                                    ['--list-table-column' as any]: headCells.length + 1, // Add 2 to bump to end of row
                                }}
                            />
                        </Fragment>
                    );
                })}
        </>
    );
};

export const ListTable = (props: props) => {
    const { rows } = props;
    const [rowData, setRowData] = useState<Record<string, unknown>[]>([]);

    type Order = 'asc' | 'desc';

    const generateHeaderArray = (rowArray: Record<string, any>[]) => {
        let keys: any[] = [];
        rowArray.forEach((row: {}) => (keys = [...keys, ...Object.keys(row)]));
        return [...new Set([...keys])];
    };

    return (
        <>
            <div
                className={styles.table}
                style={
                    props.addSpace == null || props.addSpace
                        ? {
                            ['--list-table-rows' as any]: rows ? rows.length : 1, // Add 1 to length to account for header row
                            ['--list-table-columns' as any]: props.headCells.length, // Add 1 to length to account for grid position starting at 1
                        }
                        : {}
                }
            >
                <TableHeader headCells={props.headCells} />
                {rows && <TableBody rows={rows} headCells={props.headCells} />}
            </div>
        </>
    );
};
